/**
 * Zoom webhooks (ZM-04). Zoom signs every event with the app's Secret Token:
 * `x-zm-signature: v0=hex(hmac-sha256(secret, "v0:{x-zm-request-timestamp}:{raw body}"))`.
 * The timestamp is in seconds; events older or newer than five minutes are refused so a
 * captured request cannot be replayed later. When an endpoint is added, Zoom sends an
 * `endpoint.url_validation` event whose plainToken must come back with its HMAC.
 */

/** How far a signed timestamp may be from now. */
export const ZOOM_SIGNATURE_TOLERANCE_MS = 5 * 60_000;

const enc = new TextEncoder();

async function hmacHex(secret: string, message: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(message)));
  return Array.from(sig, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Constant-time string comparison. */
function same(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** True when the signature matches the raw body and the timestamp is within tolerance. */
export async function verifyZoomSignature(
  secret: string,
  req: { signature: string | null | undefined; timestamp: string | null | undefined; rawBody: string },
  now: Date,
): Promise<boolean> {
  if (!req.signature || !req.timestamp || !/^\d{1,13}$/.test(req.timestamp)) return false;
  if (Math.abs(now.getTime() - Number(req.timestamp) * 1000) > ZOOM_SIGNATURE_TOLERANCE_MS) return false;
  const expected = `v0=${await hmacHex(secret, `v0:${req.timestamp}:${req.rawBody}`)}`;
  return same(req.signature, expected);
}

/** The answer to Zoom's `endpoint.url_validation` challenge. */
export async function zoomUrlValidation(
  secret: string,
  plainToken: string,
): Promise<{ plainToken: string; encryptedToken: string }> {
  return { plainToken, encryptedToken: await hmacHex(secret, plainToken) };
}
